import { styled } from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react";
import type { FocusEventHandler, ReactNode } from "react";
import { Suspense, lazy } from "react";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TextField } from "@/components/TextField/TextField";

import { DialCodeButton } from "./DialCodeButton";
import type { PhoneCountry } from "./types";
import { usePhoneInput } from "./usePhoneInput";

const CountryPickerDrawer = lazy(() => import("./CountryPickerDrawer"));

const StyledTelTextField = styled(TextField)(() => ({
  "& .MuiInputLabel-root": {
    color: "var(--color-text-placeholder)",
    fontSize: "16px",
    lineHeight: "22px",
    "&.Mui-focused": { color: "var(--color-text-placeholder)" },
  },
  "& .MuiFormHelperText-root": {
    marginInline: "0",
  },
}));

export type TelInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  label: ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  fullWidth?: boolean;
  autoComplete?: string;
  defaultCountry?: PhoneCountry;
};

export function TelInput({
  value,
  onChange,
  onBlur,
  label,
  error,
  helperText,
  fullWidth,
  autoComplete = "tel",
  defaultCountry = "IQ",
}: TelInputProps) {
  const {
    inputRef,
    country,
    nationalDigits,
    isValid,
    drawerOpen,
    setDrawerOpen,
    handleChange,
    handleSelectCountry,
  } = usePhoneInput({ value, onChange, defaultCountry });

  return (
    <>
      <div className="flex items-start gap-2">
        <DialCodeButton country={country} onOpen={() => setDrawerOpen(true)} />
        <StyledTelTextField
          inputRef={inputRef}
          value={nationalDigits}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          label={label}
          error={error}
          helperText={<span aria-live="polite">{helperText ?? " "}</span>}
          fullWidth={fullWidth}
          autoComplete={autoComplete}
          inputProps={{ inputMode: "numeric" }}
          endAdornment={
            isValid ? (
              <CheckCircleIcon
                size={16}
                data-testid="tel-input-valid-icon"
                className="fill-core-accent-500"
                aria-hidden="true"
              />
            ) : undefined
          }
        />
      </div>
      <Suspense
        fallback={
          <PageLoader>
            <AnimatedLoader icon="buildings" />
          </PageLoader>
        }
      >
        <CountryPickerDrawer
          open={drawerOpen}
          selectedCountry={country}
          onSelect={handleSelectCountry}
          onClose={() => setDrawerOpen(false)}
        />
      </Suspense>
    </>
  );
}
